import { uppercaseConversionCapture } from '../../utils/conversionCaptureText';
import React, { useMemo } from 'react';
import SearchableCatalogSelect from '../common/SearchableCatalogSelect';
import {
  formatConversionDowntimeValidation,
  validateConversionDowntime,
} from '../../utils/conversionWorkOrderValidation';
import ConversionValidationSummary from './ConversionValidationSummary';

const getDowntimeCode = (option) => option.code;
const getDowntimeLabel = (option) => `${option.code} - ${option.name}`;
const getDowntimeAliases = (option) => [option.code, option.name];

const toIntOrNull = (v) => {
  if (v === '' || v == null) return null;
  const n = parseInt(String(v), 10);
  return Number.isFinite(n) ? n : null;
};

// Temporary ID counter for new events (always negative to distinguish from backend IDs)
// Backend IDs are positive auto-increment, temp IDs are negative and decrement
let tempIdCounter = -1;
const getTempId = () => {
  const id = tempIdCounter;
  tempIdCounter--;
  return id;
};

const emptyEvent = () => ({
  id: getTempId(),
  work_order: '',
  code: '',
  minutes: null,
  quantity: null,
  scrap: null,
  notes: ''
});

const ConversionDowntimeGrid = ({ events, downtimeCodes, readOnly, onChange, rows = [] }) => {
  const list = useMemo(() => (Array.isArray(events) ? events : []), [events]);
  const codes = useMemo(() => (Array.isArray(downtimeCodes) ? downtimeCodes : []), [downtimeCodes]);
  
  // Get unique work orders from production rows
  const availableWorkOrders = useMemo(() => {
    const rowsList = Array.isArray(rows) ? rows : [];
    const workOrders = rowsList
      .map((r) => r.work_order)
      .filter((wo) => wo && String(wo).trim() !== '')
      .filter((value, index, self) => self.indexOf(value) === index) // unique
      .sort();
    return workOrders;
  }, [rows]);

  const add = () => onChange?.([...list, emptyEvent()]);
  const del = (id) => {
    if (!window.confirm('¿Eliminar este evento?')) return;
    onChange?.(list.filter((e) => e.id !== id));
  };
  const setEvt = (id, patch) => onChange?.(list.map((e) => (e.id === id ? { ...e, ...uppercaseConversionCapture(patch) } : e)));

  const totalMinutes = useMemo(() => list.reduce((acc, e) => acc + (toIntOrNull(e.minutes) || 0), 0), [list]);
  const totalHours = useMemo(() => (totalMinutes > 0 ? totalMinutes / 60 : 0), [totalMinutes]);
  const validation = useMemo(() => validateConversionDowntime(list, rows), [list, rows]);
  const validationMessages = useMemo(() => formatConversionDowntimeValidation(validation), [validation]);
  const validationGroups = useMemo(() => validationMessages.length > 0 ? [{
    key: 'labor-validation',
    title: 'Revisa Mano de Obra A/X',
    messages: validationMessages,
  }] : [], [validationMessages]);
  const totalQuantity = validation.totalQuantity;
  const totalScrap = validation.totalScrap;
  const productionTotals = {
    bagsGood: validation.productionBagsGood,
    scrap: validation.productionScrap,
  };
  const quantityMismatch = validation.quantityMismatch;
  const scrapMismatch = validation.scrapMismatch;
  const missingMinutesRows = useMemo(
    () => new Set(validation.missingMinutesRows),
    [validation]
  );
  const missingWorkOrderRows = useMemo(
    () => new Set(validation.missingWorkOrderRows),
    [validation]
  );
  const incompleteRows = useMemo(
    () => new Set(validation.incompleteRows),
    [validation]
  );

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm text-slate-700">
          Eventos de tiempo muerto (A/X). Total DT: <span className="font-extrabold">{totalMinutes.toLocaleString()} min</span>
        </div>
        <button
          type="button"
          onClick={add}
          disabled={readOnly}
          className="px-3 py-2 rounded-lg bg-slate-900 text-white font-semibold shadow hover:bg-black disabled:bg-gray-300 disabled:text-gray-600 disabled:cursor-not-allowed"
        >
          + Agregar evento
        </button>
      </div>

      <ConversionValidationSummary groups={validationGroups} />

      <div className="overflow-x-auto rounded-xl border border-slate-200">
        <table className="min-w-[980px] w-full text-sm">
          <thead className="bg-slate-50 text-slate-700">
            <tr>
              <th className="px-3 py-2 text-left font-bold">Work Order</th>
              <th className="px-3 py-2 text-left font-bold">Código T muerto</th>
              <th className="px-3 py-2 text-right font-bold">Minutos</th>
              <th className="px-3 py-2 text-right font-bold">Horas</th>
              <th className="px-3 py-2 text-right font-bold">Cantidad</th>
              <th className="px-3 py-2 text-right font-bold">Scrap</th>
              <th className="px-3 py-2 text-left font-bold">Notas</th>
              <th className="px-3 py-2 text-center font-bold">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {list.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-4 py-10 text-center text-slate-500">
                  Sin registros. Agrega al menos un renglón de Mano de Obra A/X.
                </td>
              </tr>
            ) : (
              list.map((e, index) => {
                const minutes = toIntOrNull(e.minutes) || 0;
                const hours = minutes > 0 ? minutes / 60 : 0;
                const minutesMissing = missingMinutesRows.has(index + 1);
                const workOrderMissing = missingWorkOrderRows.has(index + 1);
                const incomplete = incompleteRows.has(index + 1);
                return (
                  <tr key={e.id} className={`border-t hover:bg-slate-50 ${minutesMissing || workOrderMissing || incomplete ? 'bg-amber-50/40' : 'bg-white'}`}>
                    <td className="px-3 py-2">
                      {availableWorkOrders.length > 0 ? (
                        <select
                          value={e.work_order || ''}
                          onChange={(ev) => setEvt(e.id, { work_order: ev.target.value })}
                          aria-label={`Work Order de Mano de Obra, renglón ${index + 1}`}
                          disabled={readOnly}
                          className={`w-full rounded-lg border px-3 py-2 text-slate-900 focus:ring-2 focus:ring-blue-500 disabled:bg-slate-50 font-mono ${workOrderMissing ? 'border-amber-400 bg-amber-50' : 'border-slate-300'}`}
                        >
                          <option value="">Seleccionar WO...</option>
                          {availableWorkOrders.map((wo) => (
                            <option key={wo} value={wo}>{wo}</option>
                          ))}
                        </select>
                      ) : (
                        <input
                          type="text"
                          value={e.work_order || ''}
                          onChange={(ev) => setEvt(e.id, { work_order: ev.target.value })}
                          aria-label={`Work Order de Mano de Obra, renglón ${index + 1}`}
                          disabled={readOnly}
                          placeholder="WO..."
                          className={`w-full rounded-lg border px-3 py-2 text-slate-900 focus:ring-2 focus:ring-blue-500 disabled:bg-slate-50 font-mono ${workOrderMissing ? 'border-amber-400 bg-amber-50' : 'border-slate-300'}`}
                        />
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {codes.length > 0 ? (
                        <SearchableCatalogSelect
                          value={e.code || ''}
                          onChange={(value) => setEvt(e.id, { code: value })}
                          options={codes}
                          getOptionValue={getDowntimeCode}
                          getOptionLabel={getDowntimeLabel}
                          getOptionAliases={getDowntimeAliases}
                          enterAsTab
                          disabled={readOnly}
                          placeholder="Buscar código o descripción…"
                          className="min-w-[15rem]"
                        />
                      ) : (
                        <input
                          type="text"
                          value={e.code || ''}
                          onChange={(ev) => setEvt(e.id, { code: ev.target.value })}
                          disabled={readOnly}
                          placeholder="Código..."
                          className="w-full rounded-lg border border-slate-300 px-3 py-2 text-slate-900 focus:ring-2 focus:ring-blue-500 disabled:bg-slate-50"
                        />
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="number"
                        value={e.minutes ?? ''}
                        onChange={(ev) => setEvt(e.id, { minutes: toIntOrNull(ev.target.value) })}
                        aria-label={`Minutos de Mano de Obra, renglón ${index + 1}`}
                        disabled={readOnly}
                        min={0}
                        className={`w-24 ml-auto block rounded-lg border px-3 py-2 text-right text-slate-900 focus:ring-2 focus:ring-blue-500 disabled:bg-slate-50 ${
                          minutesMissing ? 'border-amber-400 bg-amber-50' : 'border-slate-300'
                        }`}
                      />
                    </td>
                    <td className="px-3 py-2 text-right font-semibold text-slate-900">
                      {hours.toFixed(2)}
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="number"
                        value={e.quantity ?? ''}
                        onChange={(ev) => setEvt(e.id, { quantity: toIntOrNull(ev.target.value) })}
                        aria-label={`Cantidad de Mano de Obra, renglón ${index + 1}`}
                        disabled={readOnly}
                        min={0}
                        className="w-24 ml-auto block rounded-lg border border-slate-300 px-3 py-2 text-right text-slate-900 focus:ring-2 focus:ring-blue-500 disabled:bg-slate-50"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="number"
                        value={e.scrap ?? ''}
                        onChange={(ev) => setEvt(e.id, { scrap: toIntOrNull(ev.target.value) })}
                        aria-label={`Scrap de Mano de Obra, renglón ${index + 1}`}
                        disabled={readOnly}
                        min={0}
                        className="w-24 ml-auto block rounded-lg border border-slate-300 px-3 py-2 text-right text-slate-900 focus:ring-2 focus:ring-blue-500 disabled:bg-slate-50"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="text"
                        value={e.notes || ''}
                        onChange={(ev) => setEvt(e.id, { notes: ev.target.value })}
                        disabled={readOnly}
                        placeholder="Opcional: describe la causa"
                        className="w-full rounded-lg border border-slate-300 px-3 py-2 text-slate-900 focus:ring-2 focus:ring-blue-500 disabled:bg-slate-50"
                      />
                    </td>
                    <td className="px-3 py-2 text-center">
                      <button
                        type="button"
                        onClick={() => del(e.id)}
                        disabled={readOnly}
                        className="px-3 py-2 rounded-lg bg-white border border-red-200 text-red-700 font-semibold hover:bg-red-50 disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
                      >
                        Borrar
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
          <tfoot className="bg-slate-50 border-t border-slate-200">
            <tr>
              <td className="px-3 py-2 font-bold text-slate-800" colSpan={2}>Totales</td>
              <td className="px-3 py-2 text-right font-extrabold text-slate-900">{totalMinutes.toLocaleString()}</td>
              <td className="px-3 py-2 text-right font-extrabold text-slate-900">{totalHours.toFixed(2)}</td>
              <td className={`px-3 py-2 text-right font-extrabold ${quantityMismatch ? 'text-red-700 bg-red-50' : 'text-slate-900'}`}>{totalQuantity.toLocaleString()}</td>
              <td className={`px-3 py-2 text-right font-extrabold ${scrapMismatch ? 'text-red-700 bg-red-50' : 'text-slate-900'}`}>{totalScrap.toLocaleString()}</td>
              <td className="px-3 py-2 text-[11px] text-slate-600" colSpan={2}>
                Ref Producción: Buenas {productionTotals.bagsGood.toLocaleString()} · Scrap {productionTotals.scrap.toLocaleString()}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

export default ConversionDowntimeGrid;
